import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Stack,
  Chip,
  Button,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Payment,
  History,
  CheckCircle,
  Edit,
  Refresh,
  Savings,
  CalendarMonth,
  Lock,
} from '@mui/icons-material';
import { Checkbox, FormControlLabel } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import api, { apiWithRetry } from '../utils/api';
import axios from 'axios';
import { API } from '../config/apiConfig';
import { jwtDecode } from 'jwt-decode';
import DashboardPageHeader from '../components/DashboardPageHeader';
import { portal, portalGradientTextSx } from '../styles/dashboardBrand';
import {
  readUserSessionProfileCache,
  writeUserSessionProfileCache,
} from '../utils/userDashboardHomeCache';
import {
  readUserRedemptionStatusCache,
  mergeUserRedemptionStatusCache,
  evaluateHasPaymentAccountFromAccounts,
} from '../utils/userRedemptionStatusCache';

const MIN_WITHDRAWAL_USD = 1;
const SAVINGS_SHARE_RATE = 0.25;
/** Display-only: savings become withdrawable on this date (no backend yet). */
const SAVINGS_WITHDRAWAL_DATE = new Date('2027-07-01T00:00:00');
const savingsTheme = {
  main: '#22C55E',
  light: '#4ADE80',
  dark: '#16A34A',
  gradientBar: 'linear-gradient(90deg, #4ADE80 0%, #22C55E 50%, #16A34A 100%)',
  gradientText: 'linear-gradient(125deg, #86EFAC 0%, #4ADE80 35%, #22C55E 70%, #16A34A 100%)',
};

function formatSavingsWithdrawalDate(date) {
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

function WithdrawalMinimumProgress({ balance, loading, minimum = MIN_WITHDRAWAL_USD }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const safeBalance = Math.max(0, Number(balance) || 0);
  const safeMinimum = Math.max(0.01, Number(minimum) || MIN_WITHDRAWAL_USD);
  const targetPercent = Math.min(100, (safeBalance / safeMinimum) * 100);
  const reached = safeBalance >= safeMinimum;
  const remaining = Math.max(0, safeMinimum - safeBalance);

  const [displayPercent, setDisplayPercent] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (loading) {
      setDisplayPercent(0);
      return undefined;
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    if (prefersReducedMotion) {
      setDisplayPercent(targetPercent);
      return undefined;
    }

    const duration = 1100;
    const start = performance.now();
    const from = 0;
    const to = targetPercent;

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setDisplayPercent(from + (to - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [loading, targetPercent]);

  const formatUsd = (value) =>
    value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const progressShimmer = {
    '@keyframes withdrawalProgressShimmer': {
      '0%': { transform: 'translateX(-120%)' },
      '100%': { transform: 'translateX(220%)' },
    },
    '@keyframes withdrawalProgressPulse': {
      '0%, 100%': { boxShadow: `0 0 10px ${alpha(portal.cyan, isDark ? 0.35 : 0.25)}` },
      '50%': { boxShadow: `0 0 18px ${alpha(portal.pink, isDark ? 0.55 : 0.35)}` },
    },
    '@keyframes withdrawalReadyPop': {
      '0%': { transform: 'scale(0.85)', opacity: 0 },
      '100%': { transform: 'scale(1)', opacity: 1 },
    },
  };

  return (
    <Box sx={{ mt: 2.25, ...progressShimmer }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: { xs: '0.68rem', sm: '0.72rem' },
            color: reached ? portal.cyan : isDark ? alpha('#fff', 0.78) : 'text.secondary',
          }}
        >
          {reached ? 'Ready to withdraw' : 'Progress to minimum withdrawal'}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 900,
            fontSize: { xs: '0.78rem', sm: '0.82rem' },
            ...portalGradientTextSx(
              {},
              reached
                ? `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.pink} 100%)`
                : `linear-gradient(90deg, ${portal.amber} 0%, ${portal.cyan} 100%)`
            ),
          }}
        >
          {loading ? '—' : `${Math.round(displayPercent)}%`}
        </Typography>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          height: 12,
          borderRadius: 999,
          bgcolor: alpha(isDark ? '#fff' : portal.indigo, isDark ? 0.1 : 0.08),
          border: '1px solid',
          borderColor: alpha(portal.cyan, isDark ? 0.22 : 0.14),
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.35,
            background: `repeating-linear-gradient(
              90deg,
              transparent,
              transparent 8px,
              ${alpha('#fff', isDark ? 0.06 : 0.12)} 8px,
              ${alpha('#fff', isDark ? 0.06 : 0.12)} 16px
            )`,
          }}
        />
        <Box
          role="progressbar"
          aria-valuenow={Math.round(displayPercent)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${Math.round(displayPercent)} percent toward minimum withdrawal`}
          sx={{
            position: 'relative',
            height: '100%',
            width: loading ? '0%' : `${displayPercent}%`,
            borderRadius: 999,
            background: reached
              ? `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.indigo} 45%, ${portal.pink} 100%)`
              : portal.gradientBar,
            boxShadow: `0 0 14px ${alpha(portal.cyan, isDark ? 0.45 : 0.3)}`,
            animation: reached
              ? 'withdrawalProgressPulse 2.4s ease-in-out infinite'
              : 'none',
            transition: loading ? 'none' : 'width 0.08s linear',
            overflow: 'hidden',
            '&::after': !reached && !loading
              ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: '40%',
                  background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.45)}, transparent)`,
                  animation: 'withdrawalProgressShimmer 2.2s ease-in-out infinite',
                }
              : {},
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: 6,
            transform: 'translateY(-50%)',
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: alpha('#fff', isDark ? 0.5 : 0.65),
            boxShadow: `0 0 6px ${alpha(portal.pink, 0.6)}`,
          }}
        />
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={0.5}
        sx={{ mt: 1.1 }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: isDark ? alpha('#fff', 0.82) : 'text.primary',
            fontSize: { xs: '0.78rem', sm: '0.8125rem' },
          }}
        >
          {loading
            ? 'Calculating progress…'
            : `$${formatUsd(safeBalance)} of $${formatUsd(safeMinimum)} minimum`}
        </Typography>
        {!loading && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {reached && (
              <CheckCircle
                sx={{
                  fontSize: 16,
                  color: portal.cyan,
                  animation: 'withdrawalReadyPop 0.45s ease-out',
                }}
              />
            )}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: reached ? portal.cyan : portal.amber,
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
              }}
            >
              {reached
                ? 'You can request a withdrawal'
                : `$${formatUsd(remaining)} to go`}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default function WithdrawHub() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isChild, setIsChild] = useState(false);

  const [user, setUser] = useState(null);
  const [countryCode, setCountryCode] = useState('');

  const [accounts, setAccounts] = useState([]); // wallet.UserPaymentAccounts
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedAccountSummary, setSelectedAccountSummary] = useState('');

  const [stripeAvailable, setStripeAvailable] = useState(false);
  const [stripeVerified, setStripeVerified] = useState(false);
  const [paypalAccount, setPaypalAccount] = useState({ name: '', email: '' });
  const [paypalSaved, setPaypalSaved] = useState(false);

  const [jazzCashNumber, setJazzCashNumber] = useState('');
  const [jazzCashCNIC, setJazzCashCNIC] = useState('');
  const [jazzCashAvailable, setJazzCashAvailable] = useState(false);
  const [jazzCashSaved, setJazzCashSaved] = useState(false);

  const [fees, setFees] = useState({ stripe: null, paypal: null, jazzcash: null });
  const [methods, setMethods] = useState([]);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [methodsLoadError, setMethodsLoadError] = useState(false);

  const [missingProfileFields, setMissingProfileFields] = useState([]);

  const [amount, setAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const [availableBalance, setAvailableBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const [paymentTermsAccepted, setPaymentTermsAccepted] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsCheckboxChecked, setTermsCheckboxChecked] = useState(false);

  const [calculatedFee, setCalculatedFee] = useState(null);
  const [finalAmount, setFinalAmount] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);

  const token = useMemo(() => localStorage.getItem('token'), []);
  const userId = useMemo(() => {
    try { return token ? jwtDecode(token).userId : null; } catch { return null; }
  }, [token]);

  const requireAccountSelected = !selectedAccountId;

  useEffect(() => {
    async function bootstrap() {
      try {
        setLoading(true);
        setErrorMsg('');

        // 0) Check if user is a child - redirect if so
        try {
          const childCheckRes = await api.get(API.checkIfUserIsChild);
          if (childCheckRes.data?.success && childCheckRes.data?.isChild) {
            setIsChild(true);
            navigate('/dashboard/engagement');
            return; // Exit early, don't load withdrawal page
          }
        } catch (err) {
          console.warn('Could not check if user is child:', err.message);
          // Continue loading page if check fails
        }

        // 1) Load user basic info once per login session
        let profilePayload = readUserSessionProfileCache(userId)?.profile;
        if (!profilePayload) {
          const userRes = await api.get(API.userProfile(userId));
          profilePayload = userRes.data || {};
          writeUserSessionProfileCache(userId, profilePayload);
        }
        const usr = profilePayload?.user || profilePayload || {};
        setUser(usr);

        // Persistent post-login cache: once a user has accepted the payment
        // terms (or completed their profile) we never need to re-fetch that
        // data on subsequent loads.
        const persistentStatus = readUserRedemptionStatusCache(userId);

        // Check payment terms acceptance status (from cache, then profile,
        // then the payment-settings endpoint as a fallback).
        let termsAccepted =
          persistentStatus?.paymentTermsAccepted === true ||
          usr.PaymentTermsAccepted === true ||
          usr.PaymentTermsAccepted === 1;

        // If not found in user profile or cache, fall back to payment settings
        if (!termsAccepted) {
          try {
            const paymentSettingsRes = await api.get(API.paymentSettings);
            const paymentSettings = paymentSettingsRes.data?.data || paymentSettingsRes.data || {};
            termsAccepted = paymentSettings.PaymentTermsAccepted === true || paymentSettings.PaymentTermsAccepted === 1;
          } catch (err) {
            // If payment settings endpoint fails, default to false (user must accept)
            console.warn('Could not load payment settings:', err.message);
          }
        }

        setPaymentTermsAccepted(termsAccepted);
        if (termsAccepted) {
          mergeUserRedemptionStatusCache(userId, { paymentTermsAccepted: true });
        }

        // 2) Country code (fallback from user if available) - needed before checking postal code requirement
        const cc = (usr.CountryCode || usr.countryCode || usr.Country || usr.country || '').toString().toUpperCase();
        setCountryCode(cc);
        const isPakistan = cc === 'PK';

        // Compute required profile fields (exclude RegionId)
        // Note: PostalCode is NOT required for Pakistan users (same as UpdateProfile.js validation)
        const variants = {
          SexId: ['SexId', 'sexId', 'GenderId', 'genderId'],
          Email: ['Email', 'email'],
          CountryOfOriginId: ['CountryOfOriginId', 'countryOfOriginId', 'CountryOfOriginID', 'CountryOfOrigin'],
          CountryOfResidenceId: ['CountryOfResidenceId', 'countryOfResidenceId', 'CountryOfResidenceID', 'CountryOfResidence'],
          EthnicityId: ['EthnicityId', 'ethnicityId'],
          DateOfBirth: ['DateOfBirth', 'dateOfBirth', 'DOB', 'BirthDate', 'birthDate'],
          AddressLine1: ['AddressLine1', 'addressLine1', 'Address', 'address', 'Address1'],
          City: ['City', 'city', 'Town'],
          // Only require postal code if NOT Pakistan (same logic as UpdateProfile.js line 416)
          ...(isPakistan ? {} : { PostalCode: ['PostalCode', 'postalCode', 'PostCode', 'Zip', 'ZipCode'] })
        };

        const isProvided = (obj, keys) => {
          for (const k of keys) {
            const v = obj?.[k];
            if (v !== undefined && v !== null && String(v).trim() !== '') return true;
          }
          return false;
        };

        const missing = Object.entries(variants)
          .filter(([label, keys]) => !isProvided(usr, keys))
          .map(([label]) => label);
        setMissingProfileFields(missing);
        if (missing.length === 0) {
          mergeUserRedemptionStatusCache(userId, { profileComplete: true });
        }

        // 3) Load existing payment accounts
        const accRes = await api.get(API.savedPaymentAccounts);
        const accs = Array.isArray(accRes.data?.accounts) ? accRes.data.accounts : [];
        setAccounts(accs);
        if (evaluateHasPaymentAccountFromAccounts(accs, cc)) {
          mergeUserRedemptionStatusCache(userId, { hasPaymentAccount: true });
        }

        // 4) Load available methods (dynamic, country-aware) with retry logic for slow connections
        // OPTIMISTIC UI: For Pakistan users, show JazzCash immediately while API loads (for slow connections)
        // This ensures JazzCash is visible even on very slow connections
        if (cc === 'PK') {
          setJazzCashAvailable(true); // Show JazzCash immediately for Pakistan users (don't wait for API)
          // Load saved JazzCash account if available from already loaded accounts
          const jc = accs.find(a => (a.Provider || a.PaymentMethodName) === 'JazzCash');
          if (jc) {
            setJazzCashNumber(jc.AccountNumber || '');
            setJazzCashCNIC(jc.CNIC || '');
            setJazzCashSaved(true); // Mark as saved
          }
        }
        
        // Start loading methods in background (non-blocking for Pakistan users)
        // For Pakistan, JazzCash is already shown, so API can load slowly without blocking UI
        setMethodsLoading(true);
        setMethodsLoadError(false);
        
        const toArray = (payload) => {
          if (Array.isArray(payload)) return payload;
          if (Array.isArray(payload?.methods)) return payload.methods;
          if (Array.isArray(payload?.data)) return payload.data;
          return [];
        };

        // Helper function for API calls with extended timeout for slow connections (especially Pakistan)
        const apiCallWithExtendedTimeout = async (url, timeoutMs = 120000) => { // Increased to 120 seconds for very slow connections
          try {
            // Create a custom axios instance with extended timeout for this specific call
            const customApi = axios.create({
              baseURL: api.defaults.baseURL,
              timeout: timeoutMs,
              headers: api.defaults.headers
            });
            
            // Add auth token
            const token = localStorage.getItem('token');
            if (token) {
              customApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }
            
            return await customApi.get(url);
          } catch (err) {
            // If timeout or connection error, try one more time with even longer timeout (180 seconds)
            if (err.message?.includes('timeout') || err.code === 'ECONNABORTED' || err.message === 'Network Error') {
              console.warn(`Request timeout/error for ${url}, retrying with extended timeout (180s)...`);
              const customApi = axios.create({
                baseURL: api.defaults.baseURL,
                timeout: 180000, // 3 minutes for very slow connections
                headers: api.defaults.headers
              });
              
              const token = localStorage.getItem('token');
              if (token) {
                customApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
              }
              
              return await customApi.get(url);
            }
            throw err;
          }
        };

        // For Pakistan users, load methods in background without blocking UI
        // JazzCash is already shown optimistically, so API can take time
        const loadPaymentMethodsAsync = async () => {
          let available = [];
          let methodsLoadSuccess = false;
          
          try {
            // Try with extended timeout for slow connections
            const res1 = await apiCallWithExtendedTimeout(API.paymentMethodsForUser);
            available = toArray(res1.data);
            if (available.length > 0) methodsLoadSuccess = true;
          } catch (err) {
            console.warn('Payment methods API call failed (will retry):', err.message);
          }
          
          // Fallbacks with extended timeout
          if (available.length === 0 && cc) {
            try {
              const res2 = await apiCallWithExtendedTimeout(API.paymentMethodsForCountryCode(cc));
              available = toArray(res2.data);
              if (available.length > 0) methodsLoadSuccess = true;
            } catch (err) {
              console.warn('Country payment methods API call failed:', err.message);
            }
          }
          if (available.length === 0) {
            try {
              const res3 = await apiCallWithExtendedTimeout(API.paymentMethods);
              available = toArray(res3.data);
              if (available.length > 0) methodsLoadSuccess = true;
            } catch (err) {
              console.warn('Payment methods fallback API call failed:', err.message);
            }
          }
          if (available.length === 0) {
            try {
              const res4 = await apiCallWithExtendedTimeout(API.availablePaymentMethods);
              available = toArray(res4.data);
              if (available.length > 0) methodsLoadSuccess = true;
            } catch (err) {
              console.warn('Available payment methods API call failed:', err.message);
            }
          }
          
          // Final fallback: load by country if user-based endpoint returns empty
          if (available.length === 0 && cc) {
            try {
              const byCountryRes = await apiCallWithExtendedTimeout(API.paymentMethodsForCountryCode(cc));
              const countryMethods = Array.isArray(byCountryRes.data?.methods) ? byCountryRes.data.methods : [];
              if (countryMethods.length > 0) {
                available = countryMethods;
                methodsLoadSuccess = true;
              }
            } catch (err) {
              console.warn('Final country payment methods API call failed:', err.message);
            }
          }
          
          setMethods(available);
          setMethodsLoading(false);
          setMethodsLoadError(!methodsLoadSuccess && available.length === 0);
          
          return { available, methodsLoadSuccess };
        };
        
        // For Pakistan users, don't wait for API - show JazzCash immediately
        // Load methods in background (non-blocking)
        let available = [];
        if (cc === 'PK') {
          // For Pakistan: Show JazzCash immediately, load everything else in background
          // This ensures JazzCash is visible even on very slow connections
          setMethodsLoading(false); // Don't show loading state - JazzCash is already visible
          
          // Start loading in background, but don't block UI - JazzCash is already shown
          loadPaymentMethodsAsync().then(({ available: loadedMethods }) => {
            // Update methods when loaded, but JazzCash is already shown
            setMethods(loadedMethods);
            const hasJazzCash = loadedMethods.some(m => (m.PaymentMethodName || m.name) === 'JazzCash');
            if (hasJazzCash) {
              const jc = accs.find(a => (a.Provider || a.PaymentMethodName) === 'JazzCash');
              if (jc && !jazzCashNumber) {
                setJazzCashNumber(jc.AccountNumber || '');
                setJazzCashCNIC(jc.CNIC || '');
                setJazzCashSaved(true); // Mark as saved
              }
            }
            
            // Load fees after methods are loaded (in background, non-blocking)
            loadFeesAsync(loadedMethods).catch(err => {
              console.warn('Fee loading failed (non-critical, JazzCash still works):', err.message);
            });
          }).catch(err => {
            console.warn('Background payment methods load failed (JazzCash still shown and functional):', err.message);
            setMethodsLoading(false);
            // Even if API fails, JazzCash is still shown and functional
            // Try to load fees with empty methods (will use defaults or show "—")
            loadFeesAsync([]).catch(() => {});
          });
          
          // Set initial methods to empty array so we don't block UI
          // JazzCash is already shown optimistically, so user can interact immediately
          setMethods([]);
          available = []; // Empty for now, will be updated when API loads
          
          // Set initial fees to empty (will be loaded in background)
          setFees({ stripe: null, paypal: null, jazzcash: null });
        } else {
          // For non-Pakistan users, wait for methods to load
          const result = await loadPaymentMethodsAsync();
          available = result.available;
          setMethods(available);
        }
        
        const hasStripe = available.some(m => (m.PaymentMethodName || m.name) === 'Stripe');
        const hasPayPal = available.some(m => (m.PaymentMethodName || m.name) === 'PayPal');
        const hasJazzCash = available.some(m => (m.PaymentMethodName || m.name) === 'JazzCash');
        
        // Fallback: if no methods provided, assume Stripe and PayPal are globally available (except Stripe hidden in PK)
        setStripeAvailable(available.length === 0 ? cc !== 'PK' : hasStripe);

        // Stripe verification status (with retry for slow connections) - non-blocking for Pakistan
        if (cc !== 'PK') {
          try {
            const kycRes = await apiWithRetry.get(API.kycStatus);
            setStripeVerified(!!kycRes.data?.verified);
          } catch (err) {
            console.warn('KYC status check failed:', err.message);
          }
        } else {
          // For Pakistan, load KYC status in background (non-blocking)
          apiWithRetry.get(API.kycStatus).then(kycRes => {
            setStripeVerified(!!kycRes.data?.verified);
          }).catch(() => {});
        }

        // PayPal
        const pp = accs.find(a => (a.Provider || a.PaymentMethodName) === 'PayPal');
        if (pp) {
          setPaypalSaved(true);
          setPaypalAccount({ name: pp.AccountName || '', email: pp.AccountNumber || pp.AccountEmail || '' });
        }

        // JazzCash availability - Always show for Pakistan users (optimistic UI)
        // If API loaded successfully and found JazzCash, keep it; otherwise keep optimistic state
        if (cc === 'PK') {
          // Always show JazzCash for Pakistan users, even if API fails or is slow
          setJazzCashAvailable(true);
          // If API loaded and found JazzCash account, update it (but don't wait)
          if (hasJazzCash) {
            const jc = accs.find(a => (a.Provider || a.PaymentMethodName) === 'JazzCash');
            if (jc && !jazzCashNumber) {
              setJazzCashNumber(jc.AccountNumber || '');
            }
          }
        } else {
          setJazzCashAvailable(hasJazzCash);
          if (hasJazzCash) {
            const jc = accs.find(a => (a.Provider || a.PaymentMethodName) === 'JazzCash');
            if (jc) setJazzCashNumber(jc.AccountNumber || '');
          }
        }

        // 5) Fees from methods if provided (only for non-Pakistan users, Pakistan loads in background)
        if (cc !== 'PK') {
          const feeFrom = (name) => {
            const m = available.find(x => (x.PaymentMethodName || x.name) === name);
            if (!m) return null;
            if (m.FeeDescription || m.fee) return m.FeeDescription || m.fee;
            const pct = m.processingFeePercent ?? m.processingFeePercentage ?? m.ProcessingFeePercent ?? m.ProcessingFeePercentage ?? m.LocalProcessingFeePercent;
            const fixed = m.processingFeeFixed ?? m.ProcessingFeeFixed ?? m.LocalProcessingFeeFixed ?? m.processingFeeFixedLocal;
            if (pct != null || fixed != null) {
              // percent may already be in percent (e.g., 2.5) or fraction (0.025)
              let pctNum = parseFloat(pct);
              if (pctNum > 0 && pctNum < 1) pctNum = pctNum * 100;
              const pctTxt = !isNaN(pctNum) ? `${pctNum.toString()}%` : null;
              const fixedTxt = fixed != null ? `${parseFloat(fixed).toFixed(2)}` : null;
              if (pctTxt && fixedTxt) return `${pctTxt} + ${fixedTxt}`;
              return pctTxt || fixedTxt;
            }
            return null;
          };
          let nextFees = { stripe: feeFrom('Stripe'), paypal: feeFrom('PayPal'), jazzcash: feeFrom('JazzCash') };

          // If any fee missing, fetch estimate via /fees/calculate for amount 100
          const ensureFee = async (methodName, key) => {
            if (nextFees[key]) return;
            const m = available.find(x => (x.PaymentMethodName || x.name) === methodName);
            if (!m) return;
            try {
              const payload = {
                amount: 100,
                paymentMethodId: m.PaymentMethodId || m.id,
              };
              const resp = await api.post(API.calculateFees, payload);
              const data = resp.data || {};
              if (data.totalFees != null || data.fees?.totalFees != null) {
                const total = parseFloat(data.totalFees ?? data.fees?.totalFees).toFixed(2);
                nextFees[key] = `~${total} on 100`;
              }
            } catch (_) {}
          };

          await ensureFee('Stripe', 'stripe');
          await ensureFee('PayPal', 'paypal');
          await ensureFee('JazzCash', 'jazzcash');

          setFees(nextFees);
        }
        
        // Helper function to load fees asynchronously (for Pakistan background loading)
        async function loadFeesAsync(methodsArray) {
          const feeFrom = (name) => {
            const m = methodsArray.find(x => (x.PaymentMethodName || x.name) === name);
            if (!m) return null;
            if (m.FeeDescription || m.fee) return m.FeeDescription || m.fee;
            const pct = m.processingFeePercent ?? m.processingFeePercentage ?? m.ProcessingFeePercent ?? m.ProcessingFeePercentage ?? m.LocalProcessingFeePercent;
            const fixed = m.processingFeeFixed ?? m.ProcessingFeeFixed ?? m.LocalProcessingFeeFixed ?? m.processingFeeFixedLocal;
            if (pct != null || fixed != null) {
              let pctNum = parseFloat(pct);
              if (pctNum > 0 && pctNum < 1) pctNum = pctNum * 100;
              const pctTxt = !isNaN(pctNum) ? `${pctNum.toString()}%` : null;
              const fixedTxt = fixed != null ? `${parseFloat(fixed).toFixed(2)}` : null;
              if (pctTxt && fixedTxt) return `${pctTxt} + ${fixedTxt}`;
              return pctTxt || fixedTxt;
            }
            return null;
          };
          
          let nextFees = { stripe: feeFrom('Stripe'), paypal: feeFrom('PayPal'), jazzcash: feeFrom('JazzCash') };

          const ensureFee = async (methodName, key) => {
            if (nextFees[key]) return;
            const m = methodsArray.find(x => (x.PaymentMethodName || x.name) === methodName);
            if (!m) return;
            try {
              const payload = {
                amount: 100,
                paymentMethodId: m.PaymentMethodId || m.id,
              };
              const resp = await api.post(API.calculateFees, payload);
              const data = resp.data || {};
              if (data.totalFees != null || data.fees?.totalFees != null) {
                const total = parseFloat(data.totalFees ?? data.fees?.totalFees).toFixed(2);
                nextFees[key] = `~${total} on 100`;
              }
            } catch (_) {}
          };

          await ensureFee('Stripe', 'stripe');
          await ensureFee('PayPal', 'paypal');
          await ensureFee('JazzCash', 'jazzcash');

          setFees(nextFees);
        }

        // 6) History
        await loadHistory();
        
        // 6) Load wallet balance
        await loadBalance();

      } catch (err) {
        setErrorMsg(err.response?.data?.error || err.message || 'Failed to load withdrawal data');
      } finally {
        setLoading(false);
      }
    }
    if (userId) bootstrap();
  }, [userId]);

  async function loadBalance() {
    try {
      setBalanceLoading(true);
      const res = await api.get(API.walletBalance);
      if (res.data?.success) {
        setAvailableBalance(res.data.balance || 0);
      }
    } catch (err) {
      console.error('Error loading balance:', err);
      setAvailableBalance(0);
    } finally {
      setBalanceLoading(false);
    }
  }

  async function loadHistory() {
    try {
      setHistoryLoading(true);
      const res = await api.get(API.withdrawalHistory);
      setHistory(Array.isArray(res.data?.history) ? res.data.history : []);
    } catch (err) {
      // non-fatal
    } finally {
      setHistoryLoading(false);
    }
  }

  function selectAccount(account) {
    setSelectedAccountId(account?.AccountID || account?.PaymentAccountID || account?.id);
    const label = `${account?.PaymentMethodName || account?.Provider || 'Account'} • ${account?.AccountName || ''}`.trim();
    setSelectedAccountSummary(label);
  }

  async function savePaypal() {
    try {
      setErrorMsg('');
      setSuccessMsg('');
      const method = methods.find(m => (m.PaymentMethodName || m.name) === 'PayPal');
      if (!method) throw new Error('PayPal not available');
      
      // Find existing PayPal account for this user
      const existingPayPalAccount = accounts.find(
        acc => (acc.PaymentMethodName || acc.Provider) === 'PayPal'
      );
      
      await api.post(API.userPaymentAccounts, {
        accountId: existingPayPalAccount?.AccountID || existingPayPalAccount?.PaymentAccountID || existingPayPalAccount?.id || null,
        userId: user?.UserID || user?.id,
        paymentMethodId: method.PaymentMethodId || method.id,
        accountName: paypalAccount.name || 'PayPal',
        accountNumber: paypalAccount.email,
        isDefault: true,
      });
      setPaypalSaved(true);
      const refreshed = await api.get(API.savedPaymentAccounts);
      setAccounts(refreshed.data?.accounts || []);
      // Persist completion so My Summary / future loads skip re-fetching.
      if (userId) {
        mergeUserRedemptionStatusCache(userId, { hasPaymentAccount: true });
      }
      setSuccessMsg('PayPal account saved successfully!');
    } catch (err) {
      console.error('❌ PayPal save error:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error response data:', err.response?.data);
      console.error('❌ Error response status:', err.response?.status);
      
      // Try multiple ways to extract the error message
      let errorMsg = 'Failed to save PayPal account';
      if (err.response?.data) {
        errorMsg = err.response.data.error || err.response.data.message || errorMsg;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      console.log('📝 Final error message to display:', errorMsg);
      setErrorMsg(errorMsg);
      setShowErrorDialog(true);
      setPaypalSaved(false);
    }
  }

  async function saveJazzCash() {
    try {
      setErrorMsg('');
      setSuccessMsg('');
      
      // For Pakistan users, if methods haven't loaded yet due to slow connection,
      // try to fetch JazzCash method on-demand
      let method = methods.find(m => (m.PaymentMethodName || m.name) === 'JazzCash');
      
      if (!method && countryCode === 'PK') {
        try {
          console.log('JazzCash method not loaded yet, fetching on-demand for Pakistan user...');
          // Create a custom axios instance with extended timeout for slow connections
          const customApi = axios.create({
            baseURL: api.defaults.baseURL,
            timeout: 120000, // 2 minutes for very slow connections
            headers: api.defaults.headers
          });
          
          const token = localStorage.getItem('token');
          if (token) {
            customApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }
          
          const methodRes = await customApi.get(API.paymentMethodsForCountryCode('PK'));
          const methodList = Array.isArray(methodRes.data) ? methodRes.data : 
                           Array.isArray(methodRes.data?.methods) ? methodRes.data.methods :
                           Array.isArray(methodRes.data?.data) ? methodRes.data.data : [];
          method = methodList.find(m => (m.PaymentMethodName || m.name) === 'JazzCash');
          
          // Update methods state if found
          if (method) {
            setMethods(prev => {
              const filtered = prev.filter(m => (m.PaymentMethodName || m.name) !== 'JazzCash');
              return [...filtered, method];
            });
          }
        } catch (err) {
          console.warn('Failed to load JazzCash method on-demand:', err.message);
          // Continue anyway - backend might accept the request without method ID
        }
      }
      
      if (!method) {
        // For Pakistan users, try to save anyway - backend might handle it
        // Otherwise, show error
        if (countryCode !== 'PK') {
          throw new Error('JazzCash not available. Please refresh the page and try again.');
        }
        // For Pakistan, try with a fallback - backend should handle JazzCash for PK
        console.warn('JazzCash method not found, attempting save anyway for Pakistan user...');
      }
      
      // Validate JazzCash account number is provided and is exactly 11 digits
      const accountNumberTrimmed = jazzCashNumber?.trim() || '';
      if (!accountNumberTrimmed) {
        throw new Error('JazzCash account number is required');
      }
      // Remove any non-numeric characters and validate length
      const accountNumberOnly = accountNumberTrimmed.replace(/\D/g, '');
      if (accountNumberOnly.length !== 11) {
        throw new Error('JazzCash account number must be exactly 11 digits');
      }

      // Validate CNIC is provided and is exactly 13 digits
      const cnicTrimmed = jazzCashCNIC?.trim() || '';
      if (!cnicTrimmed) {
        throw new Error('CNIC is required for JazzCash accounts');
      }
      // Remove any non-numeric characters and validate length
      const cnicNumbersOnly = cnicTrimmed.replace(/\D/g, '');
      if (cnicNumbersOnly.length !== 13) {
        throw new Error('CNIC must be exactly 13 digits');
      }

      // ✅ Check for existing JazzCash account (only one account per payment method)
      const existingJazzCashAccount = accounts.find(
        acc => (acc.PaymentMethodName || acc.Provider) === 'JazzCash'
      );

      await api.post(API.userPaymentAccounts, {
        accountId: existingJazzCashAccount?.AccountID || existingJazzCashAccount?.PaymentAccountID || existingJazzCashAccount?.id || null,
        userId: user?.UserID || user?.id,
        paymentMethodId: method?.PaymentMethodId || method?.id,
        accountName: 'JazzCash',
        accountNumber: accountNumberOnly, // Use validated numbers-only version
        cnic: cnicNumbersOnly, // Use validated numbers-only version
        isDefault: true,
      });
      
      const refreshed = await api.get(API.savedPaymentAccounts);
      setAccounts(refreshed.data?.accounts || []);
      setJazzCashSaved(true); // Mark as saved after successful save
      // Persist completion so My Summary / future loads skip re-fetching.
      if (userId) {
        mergeUserRedemptionStatusCache(userId, { hasPaymentAccount: true });
      }
      setSuccessMsg('JazzCash account saved successfully!');
    } catch (err) {
      console.error('❌ JazzCash save error:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error response data:', err.response?.data);
      console.error('❌ Error response status:', err.response?.status);
      
      // Try multiple ways to extract the error message
      let errorMsg = 'Failed to save JazzCash account. Please try again.';
      if (err.response?.data) {
        errorMsg = err.response.data.error || err.response.data.message || errorMsg;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      console.log('📝 Final error message to display:', errorMsg);
      setErrorMsg(errorMsg);
      setShowErrorDialog(true);
      setJazzCashSaved(false);
    }
  }

  async function startStripeVerification() {
    try {
      const res = await api.post(API.kycStart);
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to start Stripe verification');
    }
  }

  async function acceptPaymentTerms() {
    try {
      setAcceptingTerms(true);
      setErrorMsg('');
      const res = await api.post(API.acceptPaymentTerms, { accepted: true });
      if (res.data?.success) {
        setPaymentTermsAccepted(true);
        if (userId) {
          mergeUserRedemptionStatusCache(userId, { paymentTermsAccepted: true });
        }
        setSuccessMsg('Payment terms accepted successfully. You can now request redemptions.');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to accept payment terms. Please try again.');
    } finally {
      setAcceptingTerms(false);
    }
  }

  // Calculate fees when amount changes (for JazzCash and PayPal)
  useEffect(() => {
    const calculateFee = async () => {
      if (!amount || parseFloat(amount) <= 0) {
        setCalculatedFee(null);
        setFinalAmount(null);
        return;
      }

      const requestAmount = parseFloat(amount);
      let fee = 0;
      let final = requestAmount;

      // Calculate fee based on selected payment method
      if (countryCode === 'PK' && selectedAccountSummary.startsWith('JazzCash')) {
        // JazzCash fee: 0.03% of the withdrawal amount (Pakistani users only)
        const feePercent = 0.0003; // 0.03%
        fee = requestAmount * feePercent;
        final = requestAmount - fee;
      } else if (selectedAccountSummary.startsWith('PayPal')) {
        // PayPal Payouts API: 2% of transaction amount (applied globally)
        // Note: US domestic is actually $0.25 flat, but using 2% globally as requested
        const feePercent = 0.02; // 2%
        fee = requestAmount * feePercent;
        final = requestAmount - fee;
      } else {
        // Other payment methods - no calculation for now
        setCalculatedFee(null);
        setFinalAmount(null);
        return;
      }

      setCalculatedFee(fee);
      setFinalAmount(final);
    };

    calculateFee();
  }, [amount, countryCode, selectedAccountSummary]);

  async function submitWithdraw() {
    let withdrawalPayload = null; // Declare outside try block for error logging
    try {
      setWithdrawing(true);
      setErrorMsg('');
      setSuccessMsg('');
      if (!selectedAccountId) {
        setErrorMsg('Please select a payout account first.');
        setShowErrorDialog(true);
        return;
      }
      if (!amount || parseFloat(amount) <= 0) {
        setErrorMsg('Please enter a valid amount.');
        setShowErrorDialog(true);
        return;
      }
      
      const requestAmount = parseFloat(amount);
      if (requestAmount > availableBalance) {
        setErrorMsg(`Insufficient balance. Available: $${availableBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, Requested: $${requestAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
        setShowErrorDialog(true);
        return;
      }
      
      // Find the selected account to determine payment method
      let selectedAccount = accounts.find(
        acc => (acc.AccountID || acc.PaymentAccountID || acc.id) === selectedAccountId
      ) || {};
      
      // Handle placeholder IDs for PayPal, Stripe, JazzCash
      // If using placeholder, find the actual saved account
      if (!selectedAccount.AccountID && !selectedAccount.PaymentAccountID && !selectedAccount.id) {
        if (selectedAccountId === 'paypal' && paypalSaved) {
          selectedAccount = accounts.find(
            acc => (acc.PaymentMethodName || acc.Provider) === 'PayPal'
          ) || {};
        } else if (selectedAccountId === 'stripe') {
          selectedAccount = accounts.find(
            acc => (acc.PaymentMethodName || acc.Provider) === 'Stripe'
          ) || {};
        } else if (selectedAccountId === 'jazzcash' && jazzCashAvailable) {
          selectedAccount = accounts.find(
            acc => (acc.PaymentMethodName || acc.Provider) === 'JazzCash'
          ) || {};
        }
      }
      
      // Use the actual account ID if we found it, otherwise use placeholder
      const actualAccountId = selectedAccount.AccountID || 
                             selectedAccount.PaymentAccountID || 
                             selectedAccount.id || 
                             selectedAccountId;
      
      // Determine payment method from selected account or summary
      const paymentMethod = selectedAccount.PaymentMethodName || 
                           selectedAccount.Provider || 
                           (selectedAccountSummary.startsWith('PayPal') ? 'PayPal' :
                            selectedAccountSummary.startsWith('Stripe') ? 'Stripe' :
                            selectedAccountSummary.startsWith('JazzCash') ? 'JazzCash' : null);
      
      // IMPORTANT: PayPal and Stripe payouts MUST always be in USD for global users
      // This ensures users worldwide receive USD payments regardless of their country
      const currencyCode = (paymentMethod === 'PayPal' || paymentMethod === 'Stripe') ? 'USD' : undefined;
      
      // Validate that we have a valid account ID (not a placeholder string)
      const isPlaceholderId = typeof actualAccountId === 'string' && 
                              ['paypal', 'stripe', 'jazzcash'].includes(actualAccountId.toLowerCase());
      const hasValidAccount = selectedAccount.AccountID || selectedAccount.PaymentAccountID || selectedAccount.id;
      
      if (isPlaceholderId && !hasValidAccount) {
        setErrorMsg('Please save your payment account details before requesting withdrawal.');
        setShowErrorDialog(true);
        return;
      }
      
      if (!actualAccountId) {
        setErrorMsg('Please select a valid payment account.');
        setShowErrorDialog(true);
        return;
      }
      
      // Calculate fee and net amount
      let feeAmount = 0;
      let netAmount = parseFloat(amount);
      
      if (paymentMethod === 'PayPal') {
        // PayPal Payouts API: 2% of transaction amount (applied globally)
        feeAmount = parseFloat(amount) * 0.02; // 2%
        netAmount = parseFloat(amount) - feeAmount;
      } else if (paymentMethod === 'JazzCash' && countryCode === 'PK') {
        feeAmount = parseFloat(amount) * 0.0003; // JazzCash: 0.03%
        netAmount = parseFloat(amount) - feeAmount;
      }
      
      // 🔍 DEBUG: Log withdrawal request payload for testing
      withdrawalPayload = {
        amount: parseFloat(amount), // Requested amount (before fee)
        fee: feeAmount, // Fee amount
        netAmount: netAmount, // Amount after fee (what user will receive)
        paymentAccountId: actualAccountId,
        ...(currencyCode ? { currencyCode } : {}), // Only include currencyCode if explicitly USD
      };
      
      console.log('🔍 [WithdrawHub] Withdrawal Request Payload:', JSON.stringify({
        ...withdrawalPayload,
        paymentMethod: paymentMethod || 'Unknown',
        selectedAccount: {
          id: selectedAccount.AccountID || selectedAccount.PaymentAccountID || selectedAccount.id,
          name: selectedAccount.AccountName,
          method: selectedAccount.PaymentMethodName || selectedAccount.Provider
        },
        currencyEnforcement: {
          detectedMethod: paymentMethod,
          isPayPalOrStripe: paymentMethod === 'PayPal' || paymentMethod === 'Stripe',
          currencyCode: currencyCode || 'Not specified (will use default)'
        }
      }, null, 2));
      
      const res = await api.post(API.withdrawalRequest, withdrawalPayload);
      setSuccessMsg(res.data?.message || 'Redemption requested successfully');
      setAmount('');
      await loadHistory();
      await loadBalance(); // Refresh balance after withdrawal
    } catch (err) {
      // Enhanced error logging to help debug 400 errors
      const errorDetails = {
        status: err.response?.status,
        statusText: err.response?.statusText,
        error: err.response?.data?.error,
        message: err.response?.data?.message,
        details: err.response?.data?.details,
        fullResponse: err.response?.data,
        requestPayload: withdrawalPayload
      };
      console.error('❌ [WithdrawHub] Withdrawal Request Error:', JSON.stringify(errorDetails, null, 2));
      console.error('❌ Error Status:', err.response?.status);
      console.error('❌ Error Message:', err.response?.data?.error || err.response?.data?.message || err.message);
      console.error('❌ Full Error Response:', err.response?.data);
      
      // Show detailed error message to user
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.response?.data?.details ||
                          (err.response?.status === 400 ? 'Invalid request. Please check your account details and try again.' : '') ||
                          err.message || 
                          'Redemption request failed';
      
      setErrorMsg(errorMessage);
      setShowErrorDialog(true);
    } finally {
      setWithdrawing(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
      <DashboardPageHeader
        icon={AccountBalanceWallet}
        title="Redemptions"
      />

      {/* Profile completion gate */}
      {missingProfileFields.length > 0 && (
        <Card sx={{ mb: 3, border: '1px dashed', borderColor: 'warning.main' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h6" 
              color="warning.main" 
              sx={{ 
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 600
              }}
            >
              Complete your profile to enable redemptions
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                wordBreak: 'break-word'
              }}
            >
              Please provide the following required fields: {missingProfileFields.join(', ')}.
            </Typography>
            <Button 
              variant="contained" 
              color="warning" 
              onClick={() => navigate('/dashboard/profile')}
              fullWidth={{ xs: true, sm: false }}
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 'auto', sm: 120 }
              }}
            >
              Go to Profile
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Payment Terms & Liability Agreement */}
      {!paymentTermsAccepted && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'warning.main', bgcolor: 'rgba(255, 152, 0, 0.05)' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h6" 
              color="warning.main" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 600
              }}
            >
              Payment Terms & Liability Agreement
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                lineHeight: 1.6
              }}
            >
              Before requesting redemptions, you must acknowledge and agree to our Payment Terms & Liability Agreement.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowTermsDialog(true)}
                sx={{ mb: 2 }}
              >
                View Full Terms & Conditions
              </Button>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsCheckboxChecked}
                  onChange={(e) => setTermsCheckboxChecked(e.target.checked)}
                  disabled={acceptingTerms}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                  I have read and agree to the Payment Terms & Liability Agreement
                </Typography>
              }
            />
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={acceptPaymentTerms}
                disabled={!termsCheckboxChecked || acceptingTerms}
                fullWidth
              >
                {acceptingTerms ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Accepting Terms...
                  </>
                ) : (
                  'Accept Terms & Continue'
                )}
              </Button>
            </Box>
            {acceptingTerms && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Accepting terms...
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Available Balance & Savings — all users */}
        <Card
          sx={{
            mb: 3,
            position: 'relative',
            borderRadius: 3.5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha(isDark ? portal.cyan : theme.palette.divider, isDark ? 0.28 : 0.16),
            background: isDark
              ? `linear-gradient(135deg, ${alpha('#1a2138', 0.96)} 0%, ${alpha('#1d1428', 0.95)} 55%, ${alpha('#240f1c', 0.96)} 100%)`
              : `linear-gradient(135deg, ${alpha(portal.cyan, 0.07)} 0%, ${alpha(portal.purple, 0.05)} 60%, ${alpha(portal.pink, 0.06)} 100%)`,
            boxShadow: isDark
              ? `0 16px 44px ${alpha('#000', 0.4)}, 0 0 36px ${alpha(portal.cyan, 0.08)}`
              : `0 14px 36px ${alpha(portal.cyan, 0.12)}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.indigo} 38%, ${portal.purple} 68%, ${portal.pink} 100%)`,
              opacity: isDark ? 0.95 : 0.85,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at 88% 18%, ${alpha(portal.pink, isDark ? 0.16 : 0.08)} 0%, transparent 55%)`,
              pointerEvents: 'none',
            },
          }}
        >
          <CardContent sx={{ p: { xs: 2.25, sm: 3 }, pt: { xs: 2.5, sm: 3 }, position: 'relative', zIndex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              spacing={1.5}
              sx={{ mb: 1.25, rowGap: 0.75 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${alpha(portal.cyan, isDark ? 0.25 : 0.18)} 0%, ${alpha(portal.purple, isDark ? 0.22 : 0.12)} 100%)`,
                  border: '1px solid',
                  borderColor: alpha(portal.cyan, isDark ? 0.4 : 0.28),
                  boxShadow: isDark ? `0 0 16px ${alpha(portal.cyan, 0.25)}` : 'none',
                  flexShrink: 0,
                }}
              >
                <AccountBalanceWallet sx={{ color: portal.cyan, fontSize: 24 }} />
              </Box>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: '0.14em',
                  fontSize: { xs: '0.72rem', sm: '0.78rem' },
                  fontWeight: 800,
                  lineHeight: 1.2,
                  flexShrink: 0,
                  ...portalGradientTextSx({}, `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.pink} 100%)`),
                }}
              >
                Available Balance
              </Typography>
              {balanceLoading ? (
                <CircularProgress size={20} sx={{ flexShrink: 0 }} />
              ) : (
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: '-0.01em',
                    fontSize: { xs: '1.35rem', sm: '2rem' },
                    lineHeight: 1.05,
                    flexShrink: 0,
                    ...portalGradientTextSx(),
                  }}
                >
                  ${availableBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              )}
            </Stack>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                color: isDark ? alpha('#fff', 0.72) : 'text.secondary',
                fontSize: { xs: '0.78rem', sm: '0.8125rem' },
                lineHeight: 1.55,
              }}
            >
              This amount is earned by inviting new members and growing your referral network.
            </Typography>
            {countryCode === 'PK' && (
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                sx={{
                  mt: 1.75,
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor: alpha(isDark ? '#fff' : portal.cyan, isDark ? 0.04 : 0.06),
                  border: '1px solid',
                  borderColor: alpha(portal.cyan, isDark ? 0.2 : 0.14),
                }}
              >
                <CalendarMonth sx={{ fontSize: 18, color: portal.cyan, mt: 0.15, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      fontSize: '0.68rem',
                      color: isDark ? alpha('#fff', 0.55) : 'text.secondary',
                    }}
                  >
                    Withdrawal schedule
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      mt: 0.35,
                      pl: 2.1,
                      '& li': {
                        fontWeight: 600,
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        color: isDark ? alpha('#fff', 0.9) : 'text.primary',
                        lineHeight: 1.5,
                        mb: 0.25,
                        '&:last-child': { mb: 0 },
                      },
                    }}
                  >
                    <li>Automatic monthly withdraw</li>
                    <li>5th of each month if $1 limit reached</li>
                    <li>JazzCash account</li>
                  </Box>
                </Box>
              </Stack>
            )}
            <WithdrawalMinimumProgress
              balance={availableBalance}
              loading={balanceLoading}
              minimum={MIN_WITHDRAWAL_USD}
            />
          </CardContent>
        </Card>

        <Card
          sx={{
            mb: 3,
            position: 'relative',
            borderRadius: 3.5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha(isDark ? savingsTheme.main : theme.palette.divider, isDark ? 0.28 : 0.16),
            background: isDark
              ? `linear-gradient(135deg, ${alpha('#141f18', 0.96)} 0%, ${alpha('#121a16', 0.95)} 55%, ${alpha('#0f1a14', 0.96)} 100%)`
              : `linear-gradient(135deg, ${alpha(savingsTheme.main, 0.07)} 0%, ${alpha(savingsTheme.light, 0.05)} 60%, ${alpha(savingsTheme.main, 0.06)} 100%)`,
            boxShadow: isDark
              ? `0 16px 44px ${alpha('#000', 0.4)}, 0 0 36px ${alpha(savingsTheme.main, 0.1)}`
              : `0 14px 36px ${alpha(savingsTheme.main, 0.12)}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: savingsTheme.gradientBar,
              opacity: isDark ? 0.95 : 0.85,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at 12% 22%, ${alpha(savingsTheme.main, isDark ? 0.14 : 0.08)} 0%, transparent 55%)`,
              pointerEvents: 'none',
            },
          }}
        >
          <CardContent sx={{ p: { xs: 2.25, sm: 3 }, pt: { xs: 2.5, sm: 3 }, position: 'relative', zIndex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              spacing={1.5}
              sx={{ mb: 1.25, rowGap: 0.75 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${alpha(savingsTheme.light, isDark ? 0.28 : 0.2)} 0%, ${alpha(savingsTheme.main, isDark ? 0.22 : 0.14)} 100%)`,
                  border: '1px solid',
                  borderColor: alpha(savingsTheme.main, isDark ? 0.42 : 0.3),
                  boxShadow: isDark ? `0 0 16px ${alpha(savingsTheme.main, 0.28)}` : 'none',
                  flexShrink: 0,
                }}
              >
                <Savings sx={{ color: savingsTheme.light, fontSize: 24 }} />
              </Box>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: '0.14em',
                  fontSize: { xs: '0.72rem', sm: '0.78rem' },
                  fontWeight: 800,
                  lineHeight: 1.2,
                  flexShrink: 0,
                  ...portalGradientTextSx({}, `linear-gradient(90deg, ${savingsTheme.light} 0%, ${savingsTheme.main} 100%)`),
                }}
              >
                Savings
              </Typography>
              {balanceLoading ? (
                <CircularProgress size={20} sx={{ flexShrink: 0 }} />
              ) : (
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: '-0.01em',
                    fontSize: { xs: '1.35rem', sm: '2rem' },
                    lineHeight: 1.05,
                    flexShrink: 0,
                    ...portalGradientTextSx({}, savingsTheme.gradientText),
                  }}
                >
                  ${((Number(availableBalance) || 0) * SAVINGS_SHARE_RATE).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              )}
            </Stack>
            <Divider sx={{ my: 2, borderColor: alpha(savingsTheme.main, isDark ? 0.22 : 0.18) }} />
            <Stack spacing={1.35}>
              {[
                {
                  icon: CalendarMonth,
                  label: 'Withdrawable from',
                  value: formatSavingsWithdrawalDate(SAVINGS_WITHDRAWAL_DATE),
                },
              ].map(({ icon: Icon, label, value }) => (
                <Stack
                  key={label}
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: alpha(isDark ? '#fff' : savingsTheme.main, isDark ? 0.04 : 0.06),
                    border: '1px solid',
                    borderColor: alpha(savingsTheme.main, isDark ? 0.16 : 0.12),
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: savingsTheme.light, mt: 0.15, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        fontSize: '0.68rem',
                        color: isDark ? alpha('#fff', 0.55) : 'text.secondary',
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        color: isDark ? alpha('#fff', 0.9) : 'text.primary',
                        lineHeight: 1.45,
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                </Stack>
              ))}
              {(() => {
                const now = new Date();
                const unlocked = now >= SAVINGS_WITHDRAWAL_DATE;
                const daysUntil = Math.max(
                  0,
                  Math.ceil((SAVINGS_WITHDRAWAL_DATE - now) / (1000 * 60 * 60 * 24))
                );
                return (
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="flex-start"
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: alpha(savingsTheme.main, isDark ? 0.08 : 0.1),
                      border: '1px solid',
                      borderColor: alpha(savingsTheme.main, isDark ? 0.28 : 0.2),
                    }}
                  >
                    {unlocked ? (
                      <CheckCircle sx={{ fontSize: 18, color: savingsTheme.light, mt: 0.15, flexShrink: 0 }} />
                    ) : (
                      <Lock sx={{ fontSize: 18, color: savingsTheme.light, mt: 0.15, flexShrink: 0 }} />
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          fontSize: '0.68rem',
                          color: isDark ? alpha('#fff', 0.55) : 'text.secondary',
                        }}
                      >
                        Status
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                          color: unlocked ? savingsTheme.light : isDark ? alpha('#fff', 0.9) : 'text.primary',
                          lineHeight: 1.45,
                        }}
                      >
                        {unlocked
                          ? 'You can withdraw your savings'
                          : `Locked until ${formatSavingsWithdrawalDate(SAVINGS_WITHDRAWAL_DATE)}`}
                      </Typography>
                      {!unlocked && daysUntil > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.35,
                            color: savingsTheme.light,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          {daysUntil.toLocaleString()} day{daysUntil === 1 ? '' : 's'} remaining
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                );
              })()}
            </Stack>
          </CardContent>
        </Card>

      {/* Select Account - Show JazzCash only for Pakistan, Stripe/PayPal for others */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Select Account</Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {/* Stripe - Hidden as not integrated yet */}
            {false && stripeAvailable && countryCode !== 'PK' && (
              <Box 
                onClick={() => {
                  if (!missingProfileFields.length) {
                    selectAccount({ PaymentMethodName: 'Stripe', AccountName: 'Stripe', id: 'stripe' });
                  }
                }}
                sx={{ 
                  p: 2, 
                  border: '2px solid', 
                  borderColor: selectedAccountSummary.startsWith('Stripe') ? 'primary.main' : 'divider', 
                  borderRadius: 2,
                  cursor: missingProfileFields.length > 0 ? 'not-allowed' : 'pointer',
                  bgcolor: selectedAccountSummary.startsWith('Stripe') ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: missingProfileFields.length > 0 ? 'divider' : 'primary.main',
                    bgcolor: missingProfileFields.length > 0 ? 'transparent' : 'action.hover',
                  }
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Stack spacing={0.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>Stripe</Typography>
                    <Typography variant="caption" color="text.secondary">Fee: {fees.stripe || '—'}</Typography>
                    <Typography variant="caption" color={stripeVerified ? 'success.main' : 'warning.main'}>
                      {stripeVerified ? 'Verified' : 'Not verified'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}>
                    {!stripeVerified && (
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          startStripeVerification();
                        }} 
                        disabled={missingProfileFields.length > 0}
                        sx={{ flex: { xs: 1, sm: 'none' } }}
                      >
                        Get verified
                      </Button>
                    )}
                    {selectedAccountSummary.startsWith('Stripe') && (
                      <CheckCircle color="primary" />
                    )}
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* PayPal - global, but NOT for Pakistan */}
            {countryCode !== 'PK' && (
            <Box 
              onClick={() => {
                if (!missingProfileFields.length) {
                  // If PayPal is saved, use the actual saved account, otherwise use placeholder
                  if (paypalSaved) {
                    const savedPayPalAccount = accounts.find(
                      acc => (acc.PaymentMethodName || acc.Provider) === 'PayPal'
                    );
                    if (savedPayPalAccount) {
                      selectAccount(savedPayPalAccount);
                    } else {
                      selectAccount({ PaymentMethodName: 'PayPal', AccountName: paypalAccount.name || 'PayPal', id: 'paypal' });
                    }
                  } else {
                    selectAccount({ PaymentMethodName: 'PayPal', AccountName: paypalAccount.name || 'PayPal', id: 'paypal' });
                  }
                }
              }}
              sx={{ 
                p: 2, 
                border: '2px solid', 
                borderColor: selectedAccountSummary.startsWith('PayPal') ? 'primary.main' : 'divider', 
                borderRadius: 2,
                cursor: missingProfileFields.length > 0 ? 'not-allowed' : 'pointer',
                bgcolor: selectedAccountSummary.startsWith('PayPal') ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: missingProfileFields.length > 0 ? 'divider' : 'primary.main',
                  bgcolor: missingProfileFields.length > 0 ? 'transparent' : 'action.hover',
                }
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                <Stack spacing={0.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>PayPal</Typography>
                    <Typography variant="caption" color="text.secondary">Fee: 2% of withdrawal amount</Typography>
                  {paypalSaved ? (
                    <Typography variant="caption" color="success.main" sx={{ wordBreak: 'break-word' }}>
                      <CheckCircle sx={{ fontSize: 14, mr: 0.5 }} /> {paypalAccount.name} • {paypalAccount.email}
                    </Typography>
                  ) : (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} onClick={(e) => e.stopPropagation()} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <TextField size="small" label="Account name" value={paypalAccount.name} onChange={e => setPaypalAccount(v => ({ ...v, name: e.target.value }))} disabled={missingProfileFields.length > 0} sx={{ width: { xs: '100%', sm: 'auto' } }} />
                      <TextField size="small" label="Email" value={paypalAccount.email} onChange={e => setPaypalAccount(v => ({ ...v, email: e.target.value }))} disabled={missingProfileFields.length > 0} sx={{ width: { xs: '100%', sm: 'auto' } }} />
                      <Button variant="outlined" size="small" onClick={(e) => {
                        e.stopPropagation();
                        savePaypal();
                      }} disabled={missingProfileFields.length > 0} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        Save
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}>
                  {paypalSaved && (
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setPaypalSaved(false);
                      }} 
                      aria-label="edit"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                  {selectedAccountSummary.startsWith('PayPal') && (
                    <CheckCircle color="primary" />
                  )}
                </Stack>
              </Stack>
            </Box>
            )}

            {/* JazzCash - PK only */}
            {jazzCashAvailable && (
              <Box 
                onClick={() => {
                  if (!missingProfileFields.length && jazzCashNumber) {
                    selectAccount({ PaymentMethodName: 'JazzCash', AccountName: 'JazzCash', id: 'jazzcash' });
                  }
                }}
                sx={{ 
                  p: 2, 
                  border: '2px solid', 
                  borderColor: selectedAccountSummary.startsWith('JazzCash') ? 'primary.main' : 'divider', 
                  borderRadius: 2,
                  cursor: (missingProfileFields.length > 0 || !jazzCashNumber) ? 'default' : 'pointer',
                  bgcolor: selectedAccountSummary.startsWith('JazzCash') ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: (missingProfileFields.length > 0 || !jazzCashNumber) ? 'divider' : 'primary.main',
                    bgcolor: (missingProfileFields.length > 0 || !jazzCashNumber) ? 'transparent' : 'action.hover',
                  }
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Stack spacing={0.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>JazzCash</Typography>
                    <Typography variant="caption" color="text.secondary">Fee: 0.03% of withdrawal amount</Typography>
                    {jazzCashSaved ? (
                      <Typography variant="caption" color="success.main" sx={{ wordBreak: 'break-word' }}>
                        <CheckCircle sx={{ fontSize: 14, mr: 0.5 }} /> {jazzCashNumber} • CNIC: {jazzCashCNIC}
                      </Typography>
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} onClick={(e) => e.stopPropagation()} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <TextField 
                          size="small" 
                          label="Account number" 
                          value={jazzCashNumber} 
                          onChange={e => {
                            // Only allow numbers, limit to 11 digits
                            const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                            setJazzCashNumber(value);
                          }} 
                          disabled={missingProfileFields.length > 0} 
                          required
                          placeholder="03001234567"
                          inputProps={{ 
                            maxLength: 11,
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                          }}
                          helperText={jazzCashNumber && jazzCashNumber.length !== 11 ? 'Account number must be exactly 11 digits' : ''}
                          error={jazzCashNumber ? jazzCashNumber.length !== 11 : false}
                          sx={{ width: { xs: '100%', sm: 'auto' } }} 
                        />
                        <TextField 
                          size="small" 
                          label="CNIC" 
                          value={jazzCashCNIC} 
                          onChange={e => {
                            // Only allow numbers, limit to 13 digits
                            const value = e.target.value.replace(/\D/g, '').slice(0, 13);
                            setJazzCashCNIC(value);
                          }} 
                          disabled={missingProfileFields.length > 0} 
                          required
                          placeholder="1234512345671"
                          inputProps={{ 
                            maxLength: 13,
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                          }}
                          helperText={jazzCashCNIC && jazzCashCNIC.length !== 13 ? 'CNIC must be exactly 13 digits' : ''}
                          error={jazzCashCNIC ? jazzCashCNIC.length !== 13 : false}
                          sx={{ width: { xs: '100%', sm: 'auto' } }} 
                        />
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            saveJazzCash();
                          }} 
                          disabled={missingProfileFields.length > 0 || !jazzCashNumber || jazzCashNumber.length !== 11 || !jazzCashCNIC || jazzCashCNIC.length !== 13}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          Save
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}>
                    {jazzCashSaved && (
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setJazzCashSaved(false);
                        }} 
                        aria-label="edit"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                    {selectedAccountSummary.startsWith('JazzCash') && (
                      <CheckCircle color="primary" />
                    )}
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* Already saved accounts list (chips) */}
            {accounts.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {accounts.map(acc => (
                  <Chip
                    key={(acc.AccountID || acc.PaymentAccountID || acc.id) + ''}
                    label={(acc.PaymentMethodName || acc.Provider) + (acc.AccountName ? ` • ${acc.AccountName}` : '')}
                    onClick={() => selectAccount(acc)}
                    color={selectedAccountId === (acc.AccountID || acc.PaymentAccountID || acc.id) ? 'primary' : 'default'}
                    variant={selectedAccountId === (acc.AccountID || acc.PaymentAccountID || acc.id) ? 'filled' : 'outlined'}
                    clickable
                  />
                ))}
              </Stack>
            )}

            {selectedAccountSummary && (
              <Typography variant="caption" color="primary.main">Selected: {selectedAccountSummary}</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Withdraw Amount - Hidden for Pakistan users */}
      {countryCode !== 'PK' && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h6"
              sx={{
                mb: 1,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Payment /> Withdraw Funds
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {selectedAccountSummary.startsWith('PayPal') && !paypalSaved && (
              <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                Please save your PayPal account details above before requesting a withdrawal.
              </Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Box sx={{ maxWidth: { xs: '100%', sm: 260 }, width: { xs: '100%', sm: 'auto' } }}>
                <TextField
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  inputProps={{ min: 0, max: availableBalance, step: '0.01' }}
                  disabled={missingProfileFields.length > 0}
                  error={amount && parseFloat(amount) > availableBalance}
                  helperText={
                    amount && parseFloat(amount) > availableBalance 
                      ? `Amount exceeds available balance ($${availableBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
                      : ''
                  }
                  sx={{ 
                    width: '100%',
                    '& .MuiInputBase-root': {
                      fontSize: { xs: '0.9375rem', sm: '1rem' }
                    }
                  }}
                />
                {/* Show net amount after fee deduction right below the input */}
                {amount && parseFloat(amount) > 0 && calculatedFee !== null && finalAmount !== null && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mt: 1, 
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      fontWeight: 500
                    }}
                  >
                    You'll receive: <span style={{ color: '#1976d2', fontWeight: 600 }}>
                      ${finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    {calculatedFee > 0 && (
                      <span style={{ fontSize: '0.75rem', display: 'block', marginTop: '4px', color: '#666' }}>
                        (Fee: ${calculatedFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })})
                      </span>
                    )}
                  </Typography>
                )}
              </Box>
              {/* Fee Calculation Display for JazzCash (Pakistani users) and PayPal */}
              {amount && parseFloat(amount) > 0 && calculatedFee !== null && finalAmount !== null && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'info.light', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'info.main',
                  minWidth: { xs: '100%', sm: 280 }
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Fee Calculation:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Requested Amount: ${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Processing Fee {selectedAccountSummary.startsWith('JazzCash') ? '(0.03%)' : '(2%)'}: ${calculatedFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '1.1rem' }}>
                    You'll Receive: ${finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                    * Fee is deducted from your withdrawal amount
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                disabled={withdrawing || requireAccountSelected || !amount || Number(amount) <= 0 || Number(amount) > availableBalance || missingProfileFields.length > 0 || (selectedAccountSummary.startsWith('PayPal') && !paypalSaved) || !paymentTermsAccepted}
                onClick={submitWithdraw}
                fullWidth={{ xs: true, sm: false }}
                sx={{ 
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: 'auto', sm: 160 },
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  py: { xs: 1.25, sm: 1.5 }
                }}
              >
                {withdrawing ? <CircularProgress size={22} color="inherit" /> : 'Request Redemption'}
              </Button>
            </Stack>
            {/* Error and Success Messages - Displayed right below redemption box */}
            {errorMsg && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErrorMsg('')}>
                {errorMsg}
              </Alert>
            )}
            {successMsg && (
              <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccessMsg('')}>
                {successMsg}
              </Alert>
            )}
            <Stack spacing={0.5} sx={{ mt: 2 }}>
              {requireAccountSelected && (
                <Typography 
                  variant="caption" 
                  color="warning.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                >
                  Select a payout account above first
                </Typography>
              )}
              {selectedAccountSummary.startsWith('PayPal') && !paypalSaved && (
                <Typography 
                  variant="caption" 
                  color="warning.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                >
                  Save your PayPal account first
                </Typography>
              )}
              {selectedAccountSummary.startsWith('PayPal') && (
                <Typography 
                  variant="caption" 
                  color="info.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                >
                  💵 Payments will be processed in USD (as your earnings are displayed in USD)
                </Typography>
              )}
              {missingProfileFields.length > 0 && (
                <Typography 
                  variant="caption" 
                  color="warning.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                >
                  Complete your profile before requesting redemptions
                </Typography>
              )}
              {!paymentTermsAccepted && (
                <Typography 
                  variant="caption" 
                  color="warning.main"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                >
                  You must accept the payment terms and liability agreement before requesting redemptions
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            <History /> Redemption History
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {historyLoading ? (
            <CircularProgress size={24} />
          ) : history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No redemptions yet.</Typography>
          ) : (
            <Stack spacing={1}>
              {history.map((h, idx) => (
                <Box key={idx} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2">
                    {h.Date || h.CreatedAt || h.createdAt} • {h.Amount} {h.CurrencyCode || h.Currency}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {h.Status} • {h.PaymentMethodName || h.Method}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Payment Terms & Liability Agreement Dialog */}
      <Dialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}>
          Payment Terms & Liability Agreement
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Redemption Terms & Conditions
          </Typography>
          <Typography variant="body2" paragraph>
            By requesting redemptions/withdrawals, you acknowledge and agree to the following terms and conditions:
          </Typography>

          <Box sx={{ pl: 2, mb: 3 }}>
            <Typography variant="body2" component="div" paragraph>
              <strong>1. Tax Liability</strong>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>You are solely responsible for reporting and paying any taxes applicable to your earnings and redemptions in your jurisdiction.</li>
                <li>The company does not provide tax advice and is not responsible for your tax obligations.</li>
                <li>You must comply with all applicable tax laws and regulations in your country of residence.</li>
                <li>You may be required to provide tax identification information depending on your jurisdiction.</li>
              </ul>
            </Typography>

            <Typography variant="body2" component="div" paragraph>
              <strong>2. Account Responsibility</strong>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>You are fully responsible for any illegal activity conducted through your account.</li>
                <li>You will be held liable for any violations of terms of service, laws, or regulations.</li>
                <li>You must maintain the security of your account credentials and notify us immediately of any unauthorized access.</li>
                <li>You are responsible for all activities that occur under your account.</li>
              </ul>
            </Typography>

            <Typography variant="body2" component="div" paragraph>
              <strong>3. Company Limitation of Liability</strong>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>The company is not responsible for your tax obligations or any illegal activities associated with your account.</li>
                <li>The company is not liable for any losses, damages, or penalties you may incur due to tax non-compliance.</li>
                <li>The company is not responsible for any legal consequences resulting from illegal activities on your account.</li>
                <li>The company reserves the right to suspend or terminate accounts involved in illegal activities.</li>
              </ul>
            </Typography>

            <Typography variant="body2" component="div" paragraph>
              <strong>4. Withdrawal Processing</strong>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>Withdrawal requests are processed within 24-48 hours of approval.</li>
                <li>Processing times may vary based on payment method and country.</li>
                <li>You are responsible for providing accurate payment account information.</li>
                <li>Failed payments due to incorrect account details are your responsibility.</li>
              </ul>
            </Typography>

            <Typography variant="body2" component="div" paragraph>
              <strong>5. Compliance & Legal Requirements</strong>
              <ul style={{ marginTop: 8, marginBottom: 8 }}>
                <li>You must comply with all applicable laws and regulations in your jurisdiction.</li>
                <li>You must not use the platform for any illegal purposes.</li>
                <li>The company may require additional verification or documentation for compliance purposes.</li>
                <li>Failure to comply with legal requirements may result in account suspension or termination.</li>
              </ul>
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Important: By accepting these terms, you acknowledge that you have read, understood, and agree to be bound by all terms and conditions stated above. 
              You understand that you are solely responsible for tax compliance and account activities.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setShowTermsDialog(false)}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog Popup */}
      <Dialog 
        open={showErrorDialog} 
        onClose={() => {
          setShowErrorDialog(false);
          setErrorMsg('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          Error
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg || 'An error occurred. Please try again.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowErrorDialog(false);
              setErrorMsg('');
            }}
            variant="contained"
            color="error"
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
